import { useMemo, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  Autocomplete,
  Filters,
  IconChain,
  PermissionDenied,
  PermissionEnum,
  SchemaFilterType,
  Table,
} from '@ui/src';
import { useAppSelector } from '@store';
import formatLinkedClientsList from './helpers/format-linked-clients-list';
import useLinkedClientsList from './hooks/use-linked-clients';
import { columnsItems, columnsLabels } from './variables/list';

import ROUTES from '../../../routes';

import cn from './styles.module.css';

const LinkedClients = () => {
  const [takeCount, setTakeCount] = useState(0);

  const scrollRef = useRef<HTMLDivElement | null>(null);

  const {
    linkedClients,
    linkedClientsPagination,
    pspPartners,
    pspIntegrations,
    linkedClientsErrorsList,
  } = useAppSelector((state) => state.externalAccounts);
  const { activePermissions } = useAppSelector((state) => state.root);

  const { search } = useLocation();

  const {
    filters,
    autocompleteList,
    organizationsIsLoading,
    onSearch,
    onFilter,
    onClear,
    errorList,
    filtersComponent,
    handleSubmit,
    isLoading,
    isFetching,
    setPage,
    selectedValue,
    setSelectedValue,
  } = useLinkedClientsList({
    scrollRef,
    pspPartners,
    linkedClients,
    search,
    takeCount,
    setTakeCount,
    pspIntegrations,
    activePermissions,
  });

  const formattedData = useMemo(() => {
    const data = formatLinkedClientsList(
      linkedClients,
      pspPartners?.map((item) => ({ name: item.companyName, value: item.id })),
    );

    return data;
  }, [linkedClients, pspPartners, filters]);

  let content;

  if (
    !activePermissions?.includes(PermissionEnum.EXTERNAL_ORGANIZATIONS_LIST)
  ) {
    content = <PermissionDenied />;
  } else {
    content = (
      <>
        <div className={cn.linkedClientsTop}>
          <Autocomplete
            name="organizationSearch"
            value={selectedValue}
            list={autocompleteList}
            placeholder="Enter customer name or company name..."
            isLoading={organizationsIsLoading}
            className={cn.searchOrganizationInput}
            onSearch={(text) => onSearch(text)}
            onChange={(e) => {
              const nextFilters: Filters = {
                ...filters,
                take: {
                  value: [takeCount.toString()],
                  type: SchemaFilterType.STRING,
                },
              };
              if (e.target.value) {
                nextFilters.organizationId = {
                  value: [e.target.value as string],
                  type: SchemaFilterType.STRING,
                };

                const organization = autocompleteList?.find(
                  (item) => item.value === e.target.value,
                );

                if (organization) {
                  setSelectedValue(organization.value as string);
                }
              } else if (nextFilters.organizationId) {
                delete nextFilters.organizationId;
              }

              if (
                e.target.value ||
                (!e.target.value && filters.organizationId)
              ) {
                onFilter({
                  ...nextFilters,
                });
              }
            }}
            onClearClick={() => {
              onClear();

              if (filters.organizationId) {
                const nextFilters: Filters = {
                  ...filters,
                  take: {
                    value: [takeCount.toString()],
                    type: SchemaFilterType.STRING,
                  },
                };

                delete nextFilters.organizationId;

                onFilter({
                  ...nextFilters,
                });
              }
            }}
            fullWidth
            withClearButton
          />
          {activePermissions.includes(
            PermissionEnum.EXTERNAL_ORGANIZATIONS_CREATE_ACTION,
          ) && (
            <div className={cn.createLinkedClientLinkWrapper}>
              <NavLink
                to={ROUTES.EXTERNAL_ACCOUNTS_LINKED_CLIENTS_CREATE}
                className={cn.createLinkedClientLink}
              >
                <IconChain width={16} height={16} />
                Link External customer
              </NavLink>
            </div>
          )}
        </div>
        <div className={cn.linkedClientsTableWrapper}>
          <Table
            columns={columnsItems}
            data={formattedData || []}
            onSort={() => {}}
            sort={{}}
            filters={filters}
            columnsLabels={columnsLabels}
            onFilterChange={(values) => {
              const nextFilters: Filters = {
                ...values,
                take: {
                  value: [takeCount.toString()],
                  type: SchemaFilterType.STRING,
                },
              };

              if (!values.organizationId && filters.organizationId) {
                nextFilters.organizationId = {
                  value: [filters.organizationId.value[0]],
                  type: SchemaFilterType.STRING,
                };
              }

              onFilter({
                ...nextFilters,
              });
            }}
            error={linkedClientsErrorsList || errorList}
            filtersComponent={filtersComponent}
            handleSubmit={handleSubmit}
            isLoading={isLoading || isFetching || takeCount === 0}
            storeKey="statements-of-accounts"
            pagination={linkedClientsPagination}
            tableBodyClassName={isLoading || isFetching ? cn.tableLoading : ''}
            tableFormClassName={cn.linkedClientsTable}
            take={takeCount.toString()}
            setPage={(data) => {
              setPage({
                ...data,
                filters: {
                  ...filters,
                  take: {
                    value: [takeCount.toString()],
                    type: SchemaFilterType.STRING,
                  },
                },
              });
            }}
            withScroll
          />
        </div>

        <Outlet />
      </>
    );
  }

  return (
    <div className={cn.linkedClientsBotton} ref={scrollRef}>
      {content}
    </div>
  );
};

export default LinkedClients;
